"use client";

import { useEffect, useState, useMemo, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  restrictToVerticalAxis,
  restrictToFirstScrollableAncestor,
} from "@dnd-kit/modifiers";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useTrendingMoviesStore } from "@/stores/trendingMoviesStore";
import { useArticleStore } from "@/stores/articleStore";
import {
  TrendingMoviesWithDetails,
  ArticleDetails,
  UpdateTrendingMoviesDto,
  TrendingType,
} from "@/types/trending-movies";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import InfiniteScroll from "@/components/ui/infinite-scroll";
import DotsLoading from "@/components/ui/dots-loading";

import {
  RefreshCw,
  Save,
  Plus,
  X,
  FileText,
  TrendingUp,
  Search,
  GripVertical,
  Eye,
  Film,
} from "lucide-react";

import { showToast } from "@/lib/toast";
import { useDebounce } from "@/hooks/useDebounce";

interface SortableMovieItemProps {
  movie: ArticleDetails;
  onRemove: () => void;
  index: number;
}

function SortableMovieItem({ movie, onRemove, index }: SortableMovieItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `trending-${movie._id}`,
    data: { movieId: movie._id, index },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
          title="Kéo để sắp xếp"
        >
          <GripVertical className="w-4 h-4 text-gray-400" />
        </div>

        {/* Index Badge */}
        <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">
          {index + 1}
        </div>

        {/* Poster */}
        <div className="flex-shrink-0">
          {movie.image?.url ? (
            <img
              src={movie.image.url}
              alt={movie.title}
              className="w-12 h-16 bg-gray-100 rounded object-cover"
            />
          ) : (
            <div className="w-12 h-16 bg-gray-100 rounded flex items-center justify-center">
              <FileText className="w-6 h-6 text-gray-400" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 truncate">
            {movie.title}
          </h4>
          <p className="text-xs text-gray-500">/{movie.slug}</p>
          {movie.viewCount && (
            <p className="text-xs text-blue-600 mt-1">
              👁️ {movie.viewCount.toLocaleString()} lượt xem
            </p>
          )}
        </div>

        {/* Remove Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
          title="Xóa khỏi danh sách"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

interface MovieSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (movies: ArticleDetails[]) => void;
  title: string;
}

function MovieSelector({
  isOpen,
  onClose,
  onSelect,
  title,
}: MovieSelectorProps) {
  const [selectedMovies, setSelectedMovies] = useState<ArticleDetails[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { articles, isLoading, fetchArticles, error, pagination } =
    useArticleStore();

  // State to manage all loaded articles
  const [allLoadedArticles, setAllLoadedArticles] = useState<ArticleDetails[]>(
    []
  );

  // Convert Article to ArticleDetails format
  const adaptedArticles = useMemo(() => {
    return articles.map(
      (article): ArticleDetails => ({
        _id: article._id,
        title: article.title || "",
        slug: article.slug,
        featuredImage: article.featuredImage,
        category:
          article.category?.map((c) => ({
            _id: c._id,
            name: c.name,
            url: c.slug,
          })) || [],
        viewCount: article.viewCount,
        film_description: article.film_description,
        createdAt: article.createdAt,
      })
    );
  }, [articles]);

  // Fetch articles when modal opens or search term changes
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setHasMore(true);
      setAllLoadedArticles([]);
      fetchArticles({
        page: 1,
        limit: 20,
        search: debouncedSearchTerm || undefined,
      }).catch((err) => {
        console.error("Error fetching articles:", err);
      });
    }
  }, [isOpen, debouncedSearchTerm, fetchArticles]);

  // Update local articles when store articles change
  useEffect(() => {
    if (currentPage === 1) {
      setAllLoadedArticles(adaptedArticles);
    } else {
      setAllLoadedArticles((prev) => {
        const existingIds = new Set(prev.map((a) => a._id));
        const newArticles = adaptedArticles.filter(
          (a) => !existingIds.has(a._id)
        );
        return [...prev, ...newArticles];
      });
    }

    if (pagination) {
      const newHasMore = currentPage < pagination.totalPages;
      setHasMore(newHasMore);
    }
  }, [adaptedArticles, currentPage, pagination]);

  // Load more articles for infinite scroll
  const loadMore = useCallback(() => {
    if (!hasMore || isLoading) return;

    const nextPage = currentPage + 1;
    fetchArticles({
      page: nextPage,
      limit: 20,
      search: debouncedSearchTerm || undefined,
    })
      .then(() => {
        setCurrentPage(nextPage);
        setHasMore(nextPage < (pagination?.totalPages || 0));
      })
      .catch((err) => {
        console.error("Error loading more articles:", err);
      });
  }, [
    currentPage,
    hasMore,
    isLoading,
    debouncedSearchTerm,
    fetchArticles,
    pagination,
  ]);

  // Use all loaded articles (accumulated from pagination)
  const filteredArticles = useMemo(() => {
    return allLoadedArticles;
  }, [allLoadedArticles, debouncedSearchTerm]);

  const handleMovieSelect = useCallback((movie: ArticleDetails) => {
    setSelectedMovies((prev) => {
      const isSelected = prev.some((m) => m._id === movie._id);
      if (isSelected) {
        return prev.filter((m) => m._id !== movie._id);
      } else {
        return [...prev, movie];
      }
    });
  }, []);

  const handleConfirm = () => {
    onSelect(selectedMovies);
    setSelectedMovies([]);
    setSearchTerm("");
    setCurrentPage(1);
    setHasMore(true);
    setAllLoadedArticles([]);
    onClose();
  };

  const handleClose = () => {
    setSelectedMovies([]);
    setSearchTerm("");
    setCurrentPage(1);
    setHasMore(true);
    setAllLoadedArticles([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Chọn phim để thêm vào danh sách thịnh hành (tối đa 10 phim)
          </DialogDescription>
        </DialogHeader>

        {/* Search Input */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Tìm kiếm phim theo tiêu đề hoặc slug..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Virtualized List */}
        <div className="flex-1 min-h-0">
          {error ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="text-red-600 text-lg font-medium mb-2">
                  Lỗi khi tải phim
                </div>
                <div className="text-gray-500 mb-4">{error}</div>
                <Button
                  onClick={() => fetchArticles({ page: 1, limit: 20 })}
                  variant="outline"
                  size="sm"
                >
                  Thử lại
                </Button>
              </div>
            </div>
          ) : (
            <div className="h-full overflow-y-auto max-h-[300px]">
              {filteredArticles.length === 0 ? (
                isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <DotsLoading
                      size="lg"
                      color="#3b82f6"
                      text="Đang tải danh sách phim..."
                    />
                  </div>
                ) : debouncedSearchTerm ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm text-gray-500">
                        Không tìm thấy phim nào với từ khóa "
                        {debouncedSearchTerm}"
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm text-gray-500">Không có phim nào</p>
                    </div>
                  </div>
                )
              ) : (
                <div className="space-y-2 p-2">
                  <InfiniteScroll
                    isLoading={isLoading}
                    hasMore={hasMore}
                    next={loadMore}
                    threshold={0.5}
                    rootMargin="100px"
                  >
                    {filteredArticles.map((movie) => {
                      const isSelected = selectedMovies.some(
                        (m) => m._id === movie._id
                      );
                      return (
                        <div
                          key={movie._id}
                          className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                            isSelected
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          onClick={() => handleMovieSelect(movie)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                              {movie.featuredImage?.url ? (
                                <img
                                  src={movie.featuredImage.url}
                                  alt={movie.title}
                                  className="w-12 h-16 bg-gray-100 rounded object-cover"
                                />
                              ) : (
                                <div className="w-12 h-16 bg-gray-100 rounded flex items-center justify-center">
                                  <FileText className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-medium text-gray-900 truncate">
                                {movie.title}
                              </h4>
                              <p className="text-xs text-gray-500">
                                /{movie.slug}
                              </p>
                              {movie.viewCount && (
                                <p className="text-xs text-blue-600 mt-1">
                                  👁️ {movie.viewCount.toLocaleString()} lượt xem
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Loading indicator for infinite scroll - at the bottom */}
                    {isLoading && hasMore && (
                      <div className="flex justify-center py-3 border-t border-gray-100 mt-2">
                        <DotsLoading
                          size="xs"
                          color="#9ca3af"
                          text="Đang tải thêm..."
                        />
                      </div>
                    )}
                  </InfiniteScroll>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <div className="text-sm text-gray-500">
            Đã chọn: {selectedMovies.length} phim | Đã tải:{" "}
            {filteredArticles.length}/{pagination?.total || 0} phim
            {searchTerm !== debouncedSearchTerm && (
              <span className="ml-2 text-blue-600">
                <RefreshCw className="w-3 h-3 inline animate-spin mr-1" />
                Đang tìm kiếm...
              </span>
            )}
            {hasMore && (
              <span className="ml-2 text-green-600">
                (Còn {pagination?.total - filteredArticles.length} phim - Cuộn
                để tải thêm)
              </span>
            )}
            {!hasMore && filteredArticles.length > 0 && (
              <span className="ml-2 text-gray-400">
                (Đã tải hết tất cả phim)
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>
              Hủy
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={selectedMovies.length === 0}
            >
              <Plus className="w-4 h-4 mr-2" />
              Thêm {selectedMovies.length} phim
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Config cho từng type
const TYPE_CONFIG = {
  [TrendingType.TRENDING]: {
    title: "Phim Thịnh Hành",
    description: "Quản lý danh sách 10 phim thịnh hành hiển thị trên trang chủ",
    badge: "Trending",
    badgeClass: "bg-orange-50 text-orange-700 ring-orange-200",
    icon: TrendingUp,
    iconClass: "text-orange-600",
  },
  [TrendingType.BANNER]: {
    title: "Banner",
    description: "Quản lý danh sách 10 phim hiển thị trên banner trang chủ",
    badge: "Banner",
    badgeClass: "bg-purple-50 text-purple-700 ring-purple-200",
    icon: Film,
    iconClass: "text-purple-600",
  },
};

export default function TrendingMoviesPage() {
  const {
    trendingMovies,
    currentType,
    isLoading,
    isUpdating,
    error,
    fetchTrendingMovies,
    updateTrendingMovies,
  } = useTrendingMoviesStore();

  const [editedTrendingMovies, setEditedTrendingMovies] =
    useState<TrendingMoviesWithDetails | null>(null);
  const [movieSelectorOpen, setMovieSelectorOpen] = useState(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load trending movies on component mount
  useEffect(() => {
    fetchTrendingMovies();
  }, [fetchTrendingMovies]);

  // Initialize edited trending movies when data loads
  useEffect(() => {
    if (trendingMovies) {
      setEditedTrendingMovies(trendingMovies);
    }
  }, [trendingMovies]);

  // Handle type change
  const handleTypeChange = (type: TrendingType) => {
    fetchTrendingMovies(type);
  };

  // Handle save changes
  const handleSave = async () => {
    if (!editedTrendingMovies) return;

    const updateDto: UpdateTrendingMoviesDto = {
      type: currentType,
      movies: editedTrendingMovies.movies.map((m) => m._id),
      position: editedTrendingMovies.position,
      isActive: editedTrendingMovies.isActive,
    };

    try {
      await updateTrendingMovies(updateDto);
      // Success message is handled in store
    } catch (error) {
      // Error is handled in store
    }
  };

  // Handle remove item from list
  const handleRemoveItem = (movieId: string) => {
    if (!editedTrendingMovies) return;

    setEditedTrendingMovies({
      ...editedTrendingMovies,
      movies: editedTrendingMovies.movies.filter(
        (movie) => movie._id !== movieId
      ),
    });
  };

  // Handle add movies to list
  const handleAddMovies = (movies: ArticleDetails[]) => {
    if (!editedTrendingMovies) return;

    const currentMovies = editedTrendingMovies.movies;
    const existingIds = new Set(currentMovies.map((movie) => movie._id));

    // Only add movies that don't already exist
    const newMovies = movies.filter((movie) => !existingIds.has(movie._id));

    // Limit to 10 movies total
    const totalMovies = currentMovies.length + newMovies.length;
    if (totalMovies > 10) {
      const maxNewMovies = 10 - currentMovies.length;
      const limitedNewMovies = newMovies.slice(0, maxNewMovies);

      setEditedTrendingMovies({
        ...editedTrendingMovies,
        movies: [...currentMovies, ...limitedNewMovies],
      });

      showToast.warning(
        `Chỉ có thể thêm tối đa 10 phim. Đã thêm ${limitedNewMovies.length} phim.`
      );
    } else {
      setEditedTrendingMovies({
        ...editedTrendingMovies,
        movies: [...currentMovies, ...newMovies],
      });

      if (newMovies.length < movies.length) {
        showToast.info(
          `Đã thêm ${newMovies.length}/${movies.length} phim (${
            movies.length - newMovies.length
          } phim đã tồn tại)`
        );
      }
    }
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    if (!editedTrendingMovies) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    const activeIndex = editedTrendingMovies.movies.findIndex(
      (movie) => `trending-${movie._id}` === activeId
    );
    const overIndex = editedTrendingMovies.movies.findIndex(
      (movie) => `trending-${movie._id}` === overId
    );

    if (activeIndex === -1 || overIndex === -1) return;

    const newMovies = arrayMove(
      editedTrendingMovies.movies,
      activeIndex,
      overIndex
    );

    setEditedTrendingMovies({
      ...editedTrendingMovies,
      movies: newMovies,
    });
  };

  // Open movie selector
  const openMovieSelector = () => {
    setMovieSelectorOpen(true);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-500">Đang tải phim thịnh hành...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-600 text-lg font-medium mb-2">
              Lỗi khi tải phim thịnh hành
            </div>
            <div className="text-gray-500 mb-4">{error}</div>
            <Button onClick={() => fetchTrendingMovies()}>Thử lại</Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
      >
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
                Quản lý {TYPE_CONFIG[currentType].title}
              </h1>
              <div className="mt-2 flex items-center gap-2">
                <p className="text-gray-500 dark:text-gray-400">
                  {TYPE_CONFIG[currentType].description}
                </p>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${TYPE_CONFIG[currentType].badgeClass}`}>
                  {TYPE_CONFIG[currentType].badge}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={() => fetchTrendingMovies()}
                variant="outline"
                disabled={isLoading}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Làm mới
              </Button>
              <Button
                onClick={handleSave}
                disabled={!editedTrendingMovies || isUpdating}
              >
                <Save className="w-4 h-4 mr-2" />
                {isUpdating ? "Đang lưu..." : "Lưu thay đổi"}
              </Button>
            </div>
          </div>

          {/* Type Tabs */}
          <div className="flex gap-2 border-b border-gray-200 pb-4">
            {Object.entries(TYPE_CONFIG).map(([type, config]) => {
              const Icon = config.icon;
              const isActive = currentType === type;
              return (
                <Button
                  key={type}
                  variant={isActive ? "default" : "outline"}
                  onClick={() => handleTypeChange(type as TrendingType)}
                  disabled={isLoading}
                  className={isActive ? "" : "text-gray-600"}
                >
                  <Icon className={`w-4 h-4 mr-2 ${isActive ? "" : config.iconClass}`} />
                  {config.title}
                </Button>
              );
            })}
          </div>

          {editedTrendingMovies && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Trending Movies List */}
              <div className="lg:col-span-3 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {(() => {
                          const Icon = TYPE_CONFIG[currentType].icon;
                          return <Icon className={`w-5 h-5 ${TYPE_CONFIG[currentType].iconClass}`} />;
                        })()}
                        {TYPE_CONFIG[currentType].title}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">
                          {editedTrendingMovies.movies.length}/10 phim
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={openMovieSelector}
                          disabled={editedTrendingMovies.movies.length >= 10}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Thêm phim
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <SortableContext
                      items={editedTrendingMovies.movies.map(
                        (movie) => `trending-${movie._id}`
                      )}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {editedTrendingMovies.movies.map((movie, index) => (
                          <SortableMovieItem
                            key={movie._id}
                            movie={movie}
                            index={index}
                            onRemove={() => handleRemoveItem(movie._id)}
                          />
                        ))}
                        {editedTrendingMovies.movies.length === 0 && (
                          <div className="text-center py-12 text-gray-500">
                            {(() => {
                              const Icon = TYPE_CONFIG[currentType].icon;
                              return <Icon className="w-12 h-12 mx-auto mb-4 text-gray-300" />;
                            })()}
                            <p className="text-lg font-medium mb-2">
                              Chưa có {TYPE_CONFIG[currentType].title.toLowerCase()}
                            </p>
                            <p className="text-sm">
                              Nhấn "Thêm phim" để bắt đầu
                            </p>
                          </div>
                        )}
                      </div>
                    </SortableContext>
                  </CardContent>
                </Card>
              </div>

              {/* Settings */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Eye className="w-5 h-5 text-green-600" />
                      Cài đặt hiển thị
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <label className="text-sm font-medium">
                          Kích hoạt hiển thị
                        </label>
                        <p className="text-xs text-gray-500">
                          Hiển thị danh sách phim thịnh hành
                        </p>
                      </div>
                      <Switch
                        checked={editedTrendingMovies.isActive}
                        onCheckedChange={(checked) =>
                          setEditedTrendingMovies({
                            ...editedTrendingMovies,
                            isActive: checked,
                          })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Thứ tự hiển thị
                      </label>
                      <Input
                        type="number"
                        value={editedTrendingMovies.position}
                        onChange={(e) =>
                          setEditedTrendingMovies({
                            ...editedTrendingMovies,
                            position: parseInt(e.target.value) || 0,
                          })
                        }
                        placeholder="0"
                        min="0"
                      />
                      <p className="text-xs text-gray-500">
                        Số nhỏ hơn sẽ hiển thị trước
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Movie Selector */}
          <MovieSelector
            isOpen={movieSelectorOpen}
            onClose={() => setMovieSelectorOpen(false)}
            onSelect={(movies) => handleAddMovies(movies)}
            title={`Thêm phim vào danh sách ${TYPE_CONFIG[currentType].title.toLowerCase()}`}
          />
        </div>
        <DragOverlay>
          {/* No custom preview for now to keep it simple */}
        </DragOverlay>
      </DndContext>
    </DashboardLayout>
  );
}
